import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Grid,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  LocalOffer as AllowanceIcon,
  LocalGasStation as FuelIcon,
  Phone as PhoneIcon,
  DriveEta as CarIcon,
  Build as MaintenanceIcon,
  FlightTakeoff as LtaIcon,
  Percent as PercentIcon,
  AccountBalance as TaxIcon,
  Work as BonusIcon,
  AccessTime as OvertimeIcon,
  Receipt as PTIcon,
} from "@mui/icons-material";
import {
  fetchSalaryStructures,
  deleteExistingSalaryStructure,
} from "store/features/company/payrollManagement/salaryStructure/salaryStructureActions";
import {
  selectSalaryStructures,
  selectSalaryStructureLoading,
} from "store/features/company/payrollManagement/salaryStructure/salaryStructureSelectors";
import AddSalaryStructureModal from "./AddSalaryStructureModal";
import EditSalaryStructureModal from "./EditSalaryStructureModal";
import salaryStructureSvg from "assets/folders/salary-structure.svg";
import viewDetailsSvg from "assets/modals/details.svg";

const SalaryStructure = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const salaryStructures = useSelector(selectSalaryStructures);
  const loading = useSelector(selectSalaryStructureLoading);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSalaryStructure, setSelectedSalaryStructure] = useState(null);
  const [viewingStructure, setViewingStructure] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [salaryStructureToDelete, setSalaryStructureToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    dispatch(fetchSalaryStructures());
  }, [dispatch]);

  const filteredSalaryStructures = salaryStructures.filter((structure) => {
    const matchesSearch =
      searchTerm === "" ||
      structure.department_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      structure.level?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      structure.grade?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || structure.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount));
  };

  const StatusBadge = ({ status }) => {
    if (status === "active") {
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          label="Active"
          size="small"
          sx={{
            backgroundColor: "#d1fae5",
            color: "#065f46",
            fontWeight: 500,
            fontSize: "0.75rem",
            height: "24px",
            ...(theme.palette.mode === "dark" && {
              backgroundColor: "rgba(16,185,129,0.25)",
              color: "#6ee7b7",
            }),
          }}
        />
      );
    }
    return (
      <Chip
        icon={<CancelIcon sx={{ fontSize: 14 }} />}
        label="Inactive"
        size="small"
        sx={{
          backgroundColor: "#fee2e2",
          color: "#991b1b",
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "24px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: "rgba(239,68,68,0.25)",
            color: "#fca5a5",
          }),
        }}
      />
    );
  };

  const handleDeleteClick = (structure) => {
    setSalaryStructureToDelete(structure);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (salaryStructureToDelete) {
      setDeleting(true);
      const result = await dispatch(
        deleteExistingSalaryStructure(salaryStructureToDelete.id),
      );
      if (result.success) {
        dispatch(fetchSalaryStructures());
        setDeleteDialogOpen(false);
        setSalaryStructureToDelete(null);
      }
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSalaryStructureToDelete(null);
  };

  const handleViewClick = (structure) => {
    setViewingStructure(structure);
    setShowViewModal(true);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={salaryStructureSvg}
              alt="Salary Structures"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                }}
              >
                Salary Structures
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  mt: 0.5,
                }}
              >
                Manage salary grades and compensation structures
              </Typography>
            </Box>
          </Stack>

          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateModal(true)}
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                borderRadius: "10px",
                px: 2.5,
                py: 1,
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Add Salary Structure
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by department, level or grade..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 400 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1}>
          {["all", "active", "inactive"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => setStatusFilter(filter)}
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                px: 2,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(statusFilter !== filter && {
                  borderColor:
                    theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                  },
                }),
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={48} sx={{ color: "#f59e0b" }} />
            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
              }}
            >
              Loading salary structures...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "auto",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    ID / Tenant
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  >
                    Department
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    Level/Grade
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    Basic Salary
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    HRA
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    Status
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSalaryStructures.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <MoneyIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography sx={{ color: "#6b7280" }}>
                        No salary structures found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSalaryStructures.map((structure, index) => (
                    <TableRow
                      key={structure.id}
                      component={motion.tr}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.5)"
                              : "#f9fafb",
                        },
                      }}
                    >
                      {/* ID / Tenant Column */}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              fontSize: "0.875rem",
                              color: "#f59e0b",
                            }}
                          >
                            #{structure.id}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#9ca3af"
                                  : "#6b7280",
                            }}
                          >
                            Tenant: {structure.tenant_id}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* Department Column */}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.875rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#ffffff"
                                  : "#111827",
                            }}
                          >
                            {structure.department_name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#9ca3af"
                                  : "#6b7280",
                            }}
                          >
                            Code: {structure.department_code} | ID:{" "}
                            {structure.department_id}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* Level/Grade Column */}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.875rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#ffffff"
                                  : "#111827",
                            }}
                          >
                            Level: {structure.level}
                          </Typography>
                          <Chip
                            label={`Grade ${structure.grade}`}
                            size="small"
                            sx={{
                              width: "fit-content",
                              backgroundColor: "#dbeafe",
                              color: "#1e40af",
                              fontSize: "0.7rem",
                              height: "22px",
                            }}
                          />
                        </Stack>
                      </TableCell>

                      {/* Basic Salary Column */}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#ffffff"
                                  : "#111827",
                            }}
                          >
                            Min: {formatCurrency(structure.basic_salary_min)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#ffffff"
                                  : "#111827",
                            }}
                          >
                            Max: {formatCurrency(structure.basic_salary_max)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: 700,
                              color: "#f59e0b",
                            }}
                          >
                            Default:{" "}
                            {formatCurrency(structure.basic_salary_default)}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* HRA Column */}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: 600,
                              color:
                                theme.palette.mode === "dark"
                                  ? "#ffffff"
                                  : "#111827",
                            }}
                          >
                            {structure.hra_value}% ({structure.hra_type})
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#e6dfdf"
                                  : "#626770",
                            }}
                          >
                            Amt:{" "}
                            {formatCurrency(
                              (parseFloat(structure.basic_salary_default) *
                                parseFloat(structure.hra_value)) /
                                100,
                            )}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* Status Column */}
                      <TableCell>
                        <StatusBadge status={structure.status} />
                      </TableCell>

                      {/* Actions Column */}
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                        >
                          <Tooltip title="View Details">
                            <IconButton
                              onClick={() => handleViewClick(structure)}
                              size="small"
                              sx={{
                                color: "#3b82f6",
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(59,130,246,0.2)"
                                      : "#eff6ff",
                                },
                              }}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => {
                                setSelectedSalaryStructure(structure);
                                setShowEditModal(true);
                              }}
                              size="small"
                              sx={{
                                color: "#f59e0b",
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(245,158,11,0.2)"
                                      : "#fffbeb",
                                },
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Inactivate">
                            <IconButton
                              onClick={() => handleDeleteClick(structure)}
                              size="small"
                              sx={{
                                color: "#ef4444",
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(239,68,68,0.2)"
                                      : "#fef2f2",
                                },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Count Summary */}
      {!loading && salaryStructures.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
          }}
        >
          <Typography variant="body2">
            Total Structures: {salaryStructures.length}
          </Typography>
          <Typography variant="body2">
            Showing: {filteredSalaryStructures.length} of{" "}
            {salaryStructures.length}
          </Typography>
        </Stack>
      )}

      {/* View Details Modal */}
      <Dialog
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        maxWidth="md"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
        }}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            overflow: "hidden",
            maxWidth: "800px",
            margin: "16px",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
                : "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        {viewingStructure && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            {/* Gradient Top Bar */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                height: "5px",
              }}
            />

            {/* Header with SVG */}
            <Box
              sx={{
                p: 2.5,
                pr: 5,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(30,41,59,0.5)"
                    : "rgba(255,251,235,0.5)",
                position: "relative",
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ alignItems: "center" }}
              >
                {" "}
                <Box
                  component="img"
                  src={viewDetailsSvg}
                  alt="Details"
                  sx={{
                    width: 70,
                    height: 70,
                    objectFit: "contain",
                    filter: "drop-shadow(0 2px 4px rgba(245,158,11,0.3))",
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  Salary Structure Details
                </Typography>
              </Stack>

              <IconButton
                onClick={() => setShowViewModal(false)}
                sx={{
                  position: "absolute",
                  top: 18,
                  right: 18,
                  width: 32,
                  height: 32,
                  borderRadius: "10px",
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  "&:hover": {
                    background: "#f59e0b",
                    color: "#ffffff",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Single Scrollable Content */}
            <Box
              sx={{
                maxHeight: "calc(80vh - 200px)",
                overflowY: "auto",
                overflowX: "hidden",
                "&::-webkit-scrollbar": {
                  width: "5px",
                },
                "&::-webkit-scrollbar-track": {
                  background:
                    theme.palette.mode === "dark" ? "#1e293b" : "#e2e8f0",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#f59e0b",
                  borderRadius: "10px",
                },
              }}
            >
              <Box sx={{ p: 3 }}>
                <Stack spacing={2.5}>
                  {/* Row 1: Basic Information + Department Details */}
                  <Grid
                    container
                    spacing={2}
                    display="flex"
                    alignItems="stretch"
                    sx={{ width: "100%", margin: 0 }}
                  >
                    <Grid item xs={12} md={12}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          height: "100%",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <Box
                            sx={{
                              width: 4,
                              height: 20,
                              borderRadius: "2px",
                              background: "#f59e0b",
                            }}
                          />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                          >
                            Basic Information
                          </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "80px" }}
                            >
                              ID
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: "#f59e0b",
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              #{viewingStructure.id}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "80px" }}
                            >
                              Tenant ID
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.tenant_id}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "80px" }}
                            >
                              Level
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.level}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "80px" }}
                            >
                              Grade
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.grade}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "80px" }}
                            >
                              Status
                            </Typography>
                            <StatusBadge status={viewingStructure.status} />
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          height: "100%",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <Box
                            sx={{
                              width: 4,
                              height: 20,
                              borderRadius: "2px",
                              background: "#f59e0b",
                            }}
                          />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                          >
                            Department Details
                          </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "120px" }}
                            >
                              Department Name
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.department_name}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "120px" }}
                            >
                              Department Code
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.department_code}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "120px" }}
                            >
                              Department ID
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.department_id}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Row 2: Salary Breakdown + Allowances */}
                  <Grid container spacing={2} sx={{ width: "100%", margin: 0 }}>
                    <Grid item xs={12} md={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <MoneyIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                          >
                            Salary Breakdown
                          </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "110px" }}
                            >
                              Basic Salary Range
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                viewingStructure.basic_salary_min,
                              )}{" "}
                              -{" "}
                              {formatCurrency(
                                viewingStructure.basic_salary_max,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "110px" }}
                            >
                              Default Basic
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: "#f59e0b",
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                viewingStructure.basic_salary_default,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "110px" }}
                            >
                              HRA
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.hra_value}% (
                              {viewingStructure.hra_type})
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <AllowanceIcon
                            sx={{ fontSize: 16, color: "#f59e0b" }}
                          />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                          >
                            Allowances
                          </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Conveyance
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                viewingStructure.conveyance_amount,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Education Allowance
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                viewingStructure.education_allowance_amount,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Transport Allowance
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                viewingStructure.transport_allowance_amount,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Fixed Allowance
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.fixed_allowance_value}% (
                              {viewingStructure.fixed_allowance_type})
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Row 3: Reimbursements + Deductions & Contributions */}
                  <Grid container spacing={3} sx={{ width: "100%", margin: 0 }}>
                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <FuelIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                          >
                            Reimbursements (Max)
                          </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Fuel
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                viewingStructure.fuel_reimbursement_max,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Telephone
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                viewingStructure.telephone_reimbursement_max,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Driver
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                viewingStructure.driver_reimbursement_max,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Vehicle Maintenance
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                viewingStructure.vehicle_maintenance_reimbursement_max,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              LTA
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                viewingStructure.lta_reimbursement_max,
                              )}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <PercentIcon
                            sx={{ fontSize: 16, color: "#f59e0b" }}
                          />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                          >
                            Deductions & Contributions
                          </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Provident Fund
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: "#f59e0b",
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.provident_fund_percentage}%
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Gratuity
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.gratuity_percentage}%
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Commission
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.commission_percentage}%
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Tax Deduction
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.tax_deduction_percentage}%
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Bonus
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingStructure.bonus_percentage}%
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Overtime Rate
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(viewingStructure.overtime_rate)}
                              /hr
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Professional Tax Slabs - Full Width */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "20px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.6)"
                          : "#fefce8",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                      }`,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <TaxIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        Professional Tax Slabs
                      </Typography>
                    </Stack>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ borderRadius: "14px", overflow: "hidden" }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow
                            sx={{
                              background:
                                theme.palette.mode === "dark"
                                  ? "#1e293b"
                                  : "#f1f5f9",
                            }}
                          >
                            <TableCell
                              sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                            >
                              Min Salary (₹)
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                            >
                              Max Salary (₹)
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                            >
                              Tax Amount (₹)
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {viewingStructure.professional_tax_slab?.slabs?.map(
                            (slab, idx) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontSize: "0.7rem" }}>
                                  {slab.min.toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.7rem" }}>
                                  {slab.max === 999999
                                    ? "Above"
                                    : slab.max.toLocaleString()}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    color: "#f59e0b",
                                  }}
                                >
                                  {slab.amount.toLocaleString()}
                                </TableCell>
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  {/* Timestamps */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "20px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.6)"
                          : "#fefce8",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                      }`,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <EventIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        Timestamps
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ gap: "16px" }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af", minWidth: "80px" }}
                          >
                            Created At :
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              textAlign: "right",
                            }}
                          >
                            {formatDateTime(viewingStructure.created_at)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ gap: "16px" }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af", minWidth: "80px" }}
                          >
                            Updated At :
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              opacity: 0.7,
                              textAlign: "right",
                            }}
                          >
                            {formatDateTime(viewingStructure.updated_at)}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>
                </Stack>
              </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={() => setShowViewModal(false)}
                variant="contained"
                sx={{
                  borderRadius: "14px",
                  px: 3,
                  py: 0.7,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </motion.div>
        )}
      </Dialog>

      {/* Modals */}
      <AddSalaryStructureModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchSalaryStructures())}
      />

      <EditSalaryStructureModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedSalaryStructure(null);
        }}
        salaryStructure={selectedSalaryStructure}
        onSuccess={() => dispatch(fetchSalaryStructures())}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
                : "#ffffff",
          },
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            height: "6px",
          }}
        />
        <Box sx={{ p: 3, position: "relative" }}>
          <IconButton
            onClick={handleDeleteCancel}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
          >
            Delete Salary Structure
          </Typography>
          <Typography sx={{ textAlign: "center", mb: 1 }}>
            Are you sure you want to delete
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#f59e0b",
              textAlign: "center",
              mb: 2,
            }}
          >
            "{salaryStructureToDelete?.department_name} -{" "}
            {salaryStructureToDelete?.level} Grade{" "}
            {salaryStructureToDelete?.grade}"
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              fullWidth
              variant="outlined"
              sx={{ borderRadius: "12px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                borderRadius: "12px",
              }}
            >
              {deleting ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Delete"
              )}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
};

export default SalaryStructure;
